import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/** Skim filter: pointer must dwell this long before MegaMenuHoverEvent fires. */
const HOVER_COMMIT_DELAY_MS = 150;

/**
 * Hover intent (Zarafae): with nothing open, a submenu opens only after the pointer has rested on its
 * item this long, so a pointer crossing the menu doesn't flash panels open. Moving from one open item
 * to another still switches at once.
 */
const HOVER_OPEN_DELAY_MS = 150;

/**
 * A custom element that manages a header menu.
 *
 * Zarafae additions: the hover-open delay above; a disclosure button beside a link whose panel opens
 * from the keyboard or touch (`[data-zf-disclosure]`, `toggleSubmenu`); Escape and a press outside the
 * menu close an open submenu, and Escape returns focus to the control that opened it.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 * @property {HTMLElement | null} activeOverflowItem - The overflow item shown when the More trigger is active.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hoverDispatchTimer;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hoverOpenTimer;

  /** Whether the open submenu was opened by the pointer rather than by focus or its button. */
  #openedByHover = false;

  /** True while focus moves back to a trigger after Escape, so that focus doesn't reopen its submenu. */
  #restoringFocus = false;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
    document.addEventListener('keydown', this.#onDocumentKeydown);
    document.addEventListener('pointerdown', this.#onDocumentPointerdown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    document.removeEventListener('keydown', this.#onDocumentKeydown);
    document.removeEventListener('pointerdown', this.#onDocumentPointerdown);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupMutationObserver();
    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;
    this.#clearHoverOpen();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
    activeOverflowItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving = Math.abs(event.movementX) >= 1 || event.movementY >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;
      const listItem = target.closest('.menu-list__list-item');
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;
    item.style.setProperty('--box-height', `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`);
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    window.clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  /**
   * Find the first overflowing menu item shown by the More trigger.
   * @returns {HTMLElement | null}
   */
  #getFirstOverflowMenuItem() {
    const menuItem = this.refs.overflowMenu?.querySelector('[slot="overflow"] [ref="menuitem"]');
    return menuItem instanceof HTMLElement ? menuItem : null;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item: at once on focus and when moving between open items; after
   * HOVER_OPEN_DELAY_MS when the pointer arrives with nothing open.
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    if (!(event.target instanceof Element) || !this.headerComponent) return;

    const listItem = event.target;

    if (event instanceof FocusEvent) {
      // An item with a disclosure button in the bar opens from that button, not when its link takes
      // focus (under More the button is hidden and the link opens it), and a trigger that gets focus
      // back after Escape stays closed.
      if (this.#restoringFocus || hasDisclosure(listItem)) return;

      this.#clearHoverOpen();
      if (this.#activateItem(listItem, true)) this.#openedByHover = false;
      return;
    }

    if (this.#state.activeItem) {
      if (this.#activateItem(listItem, false)) this.#openedByHover = true;
      return;
    }

    this.#clearHoverOpen();
    this.#hoverOpenTimer = setTimeout(() => {
      this.#hoverOpenTimer = undefined;
      if (!listItem.isConnected || !listItem.matches(':hover')) return;
      if (this.#activateItem(listItem, false)) this.#openedByHover = true;
    }, HOVER_OPEN_DELAY_MS);
  };

  /**
   * Open or close a submenu from its disclosure button (keyboard, touch and click).
   * @param {MouseEvent} event
   */
  toggleSubmenu(event) {
    if (!(event.target instanceof Element)) return;

    const listItem = event.target.closest('.menu-list__list-item');
    const item = findMenuItem(listItem);
    if (!listItem || !item) return;

    this.#clearHoverOpen();

    if (this.#state.activeItem !== item) {
      if (this.#activateItem(listItem, true)) this.#openedByHover = false;
      return;
    }

    // A mouse click on the button of a panel that hover has already opened meant "open", so it stays
    // open and the next click closes it. Keyboard clicks (detail 0) always toggle.
    if (this.#openedByHover && event.detail > 0) {
      this.#openedByHover = false;
      return;
    }

    this.#deactivate(item);
  }

  /**
   * Open a menu item's submenu (or the More list) now.
   * @param {Element} listItem - The menu list item.
   * @param {boolean} immediate - Dispatch MegaMenuHoverEvent at once instead of after the skim delay.
   * @returns {boolean} Whether a different item became active.
   */
  #activateItem(listItem, immediate) {
    if (!this.headerComponent) return false;

    const isMoreTrigger = listItem.slot === 'more';
    const item = findMenuItem(listItem);
    const overflowItem = isMoreTrigger ? this.#getFirstOverflowMenuItem() : null;

    if (!item || item == this.#state.activeItem) return false;

    const isDefaultSlot = listItem.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;
    const previouslyActiveOverflowItem = this.#state.activeOverflowItem;

    if (previouslyActiveItem) {
      setItemExpanded(previouslyActiveItem, false);
    }
    if (previouslyActiveOverflowItem && previouslyActiveOverflowItem !== previouslyActiveItem) {
      setItemExpanded(previouslyActiveOverflowItem, false);
    }

    this.#state.activeItem = item;
    this.#state.activeOverflowItem = overflowItem;
    this.ariaExpanded = 'true';
    setItemExpanded(item, true);
    if (overflowItem && overflowItem !== item) {
      setItemExpanded(overflowItem, true);
    }

    const overflowItemSubmenu = isMoreTrigger ? findSubmenu(overflowItem) : null;
    let submenu = findSubmenu(item) || (overflowItemSubmenu ? this.overflowMenu : null);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      clearTimeout(this.#hoverDispatchTimer);
      this.#hoverDispatchTimer = undefined;
      const committedItem = item;
      if (immediate) {
        this.dispatchEvent(new MegaMenuHoverEvent());
      } else {
        this.#hoverDispatchTimer = setTimeout(() => {
          this.#hoverDispatchTimer = undefined;
          if (this.#state.activeItem === committedItem) {
            this.dispatchEvent(new MegaMenuHoverEvent());
          }
        }, HOVER_COMMIT_DELAY_MS);
      }

      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    const headerVisibleHeight = this.#getHeaderVisibleHeight();

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight, headerVisibleHeight);
    this.style.setProperty('--submenu-opacity', '1');
    this.#startPointerTracking(item, previouslyActiveItem);

    return true;
  }

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    if (event.type === 'pointerleave') this.#clearHoverOpen();

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Element && Boolean(event.relatedTarget.closest('[slot="overflow"]'));
    // Focus moving between a link and the disclosure button beside it stays inside the open item.
    const isMovingWithinItem =
      event.type === 'blur' &&
      event.relatedTarget instanceof Node &&
      Boolean(this.#state.activeItem?.parentElement?.contains(event.relatedTarget));

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu || isMovingWithinItem) {
      if (this.#state.activeItem) {
        this.#stopPointerTracking(this.#state.activeItem);
      }
      return;
    }

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0, 0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item) || (item.closest('[slot="more"]') ? this.overflowMenu : null);
    const activeOverflowItem = this.#state.activeOverflowItem;

    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.#state.activeOverflowItem = null;
    this.#openedByHover = false;
    this.ariaExpanded = 'false';
    setItemExpanded(item, false);
    if (activeOverflowItem && activeOverflowItem !== item) {
      setItemExpanded(activeOverflowItem, false);
    }

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  /**
   * Escape closes an open submenu. When focus was inside the menu (in the panel, or on another item),
   * it returns to the control that opens the submenu: the disclosure button, else the item itself.
   * Under More the item closes with the overflow list, so focus returns to the More trigger instead.
   * @param {KeyboardEvent} event
   */
  #onDocumentKeydown = (event) => {
    if (event.key !== 'Escape') return;

    this.#clearHoverOpen();

    const item = this.#state.activeItem;
    if (!item) return;

    const listItem = item.parentElement;
    const inOverflow = listItem?.slot === 'overflow';
    const moreTrigger = this.querySelector('[slot="more"] > [ref="menuitem"]');
    let trigger = item;
    if (inOverflow && moreTrigger instanceof HTMLElement) {
      trigger = moreTrigger;
    } else if (listItem && hasDisclosure(listItem)) {
      trigger = findDisclosure(listItem) ?? item;
    }
    const focused = document.activeElement;
    // Focus on the trigger, or on a bar item's own link, stays visible after closing; anything else moves.
    const focusStaysVisible = focused === trigger || (focused === item && !inOverflow);
    const shouldMoveFocus = focused instanceof Node && this.contains(focused) && !focusStaysVisible;

    this.#deactivate(item);
    if (this.#state.activeItem) return;

    if (shouldMoveFocus && trigger instanceof HTMLElement) {
      this.#restoringFocus = true;
      try {
        trigger.focus();
      } finally {
        this.#restoringFocus = false;
      }
    }
  };

  /**
   * A press outside the menu closes an open submenu.
   * @param {PointerEvent} event
   */
  #onDocumentPointerdown = (event) => {
    if (!this.#state.activeItem) return;
    if (event.composedPath().includes(this)) return;

    this.#clearHoverOpen();
    this.#deactivate();
  };

  /**
   * Cancel a pending hover-intent open.
   */
  #clearHoverOpen() {
    clearTimeout(this.#hoverOpenTimer);
    this.#hoverOpenTimer = undefined;
  }

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Read the visible header height before submenu height writes invalidate layout.
   * @returns {number}
   */
  #getHeaderVisibleHeight() {
    if (!this.headerComponent) return 0;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    return isOverlapSituation && this.headerComponent.offsetHeight > 0
      ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
      : this.headerComponent.offsetHeight;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   * @param {number} headerVisibleHeight
   */
  #setFullOpenHeaderHeight(submenuHeight, headerVisibleHeight) {
    if (!this.headerComponent) return;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + headerVisibleHeight;

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}

/**
 * The disclosure button beside a menu item's link (the Zarafae looks panel), if it has one.
 * @param {Element | null | undefined} listItem - The menu list item.
 * @returns {HTMLElement | null}
 */
function findDisclosure(listItem) {
  const button = listItem?.querySelector(':scope > [data-zf-disclosure]');
  return button instanceof HTMLElement ? button : null;
}

/**
 * Whether a menu list item opens from a disclosure button: it has one and sits in the bar. Under More
 * (slot="overflow") the button is hidden and the link opens the panel, as Horizon's links do.
 * @param {Element} listItem
 * @returns {boolean}
 */
function hasDisclosure(listItem) {
  return listItem.slot !== 'overflow' && Boolean(findDisclosure(listItem));
}

/**
 * Set a menu item's open state. Horizon's links carry aria-expanded themselves. A link beside a
 * disclosure button stays a plain link: the button always carries the state (the CSS keys on it), and
 * the link carries it only while open under More, where the button is hidden.
 * @param {HTMLElement | null | undefined} item - The menu item (`[ref="menuitem"]`).
 * @param {boolean} expanded
 */
function setItemExpanded(item, expanded) {
  if (!item) return;

  const listItem = item.parentElement;
  const disclosure = findDisclosure(listItem);

  if (!disclosure) {
    item.ariaExpanded = String(expanded);
    return;
  }

  disclosure.setAttribute('aria-expanded', String(expanded));
  if (expanded && listItem?.slot === 'overflow') {
    item.setAttribute('aria-expanded', 'true');
  } else {
    item.removeAttribute('aria-expanded');
  }
}
